import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { AppEquipmentTypeSelectField, AppInputField, AppPromptsField, AppSelectField } from '@/form/FormUI';
import type { EquipmentNodeType, NodeFormProps } from '@/nodes';
import { appEquipmentConfigState, equipmentTypesState, equipmentTypeState, useSavedFormValues } from '@/state';
import { useAsyncAtomValue } from '@/state/loadableHooks';
import { extractFieldErrors } from '@/util/extractFieldErrors';
import { zodResolver } from '@hookform/resolvers/zod';
import { EquipmentNodeDataSchema, type EquipmentNodeDataType, type EquipmentTypeId } from '@rock/types';
import { visibleCoreFields } from '@rock/util';
import { type FC, useCallback, useEffect } from 'react';
import { type FieldErrors, useFieldArray, useForm, useWatch } from 'react-hook-form';
import { toast } from 'sonner';
import type { z } from 'zod';

type FormInput = z.input<typeof EquipmentNodeDataSchema>;

const EquipmentForm: FC<NodeFormProps<EquipmentNodeType>> = ({ spaceId, flowId, node, updateNode, cancelUpdate }) => {
    const equipmentTypes = useAsyncAtomValue(equipmentTypesState(spaceId));
    const [formState, setFormState] = useSavedFormValues<FormInput>({ flowId, nodeId: node.id });
    const form = useForm<FormInput, unknown, EquipmentNodeDataType>({
        resolver: zodResolver(EquipmentNodeDataSchema),
        defaultValues: formState || node.data,
    });

    const shownFields = useFieldArray({
        control: form.control,
        name: 'fieldDisplay.shownFields',
    });

    const hiddenFields = useFieldArray({
        control: form.control,
        name: 'fieldDisplay.hiddenFields',
    });

    const watchedFields = useWatch({ control: form.control });
    useEffect(() => {
        setFormState(watchedFields);
    }, [setFormState, watchedFields]);

    const appEquipmentConfig = useAsyncAtomValue(appEquipmentConfigState);
    const type = useAsyncAtomValue(equipmentTypeState(`${watchedFields.equipmentTypeId || '-'}` as EquipmentTypeId));
    const scope = type?.scope ?? 'normal';
    const coreFields = visibleCoreFields(appEquipmentConfig?.coreFields ?? [], { scope, typeId: type?.id });
    const typeFields = type?.inputFields;
    const allFields = [...coreFields, ...(typeFields ?? [])];
    const fieldDisplay = watchedFields.fieldDisplay;

    const onSubmit = useCallback(
        (values: EquipmentNodeDataType) => {
            setFormState(null);
            updateNode({
                ...node,
                data: values,
            });
        },
        [setFormState, node, updateNode],
    );

    const onInvalid = useCallback((errors: FieldErrors<FormInput>) => {
        toast.error('Validation Errors', {
            description: extractFieldErrors(errors).join(', '),
        });
    }, []);

    const onCancel = useCallback(() => {
        setFormState(null);
        cancelUpdate();
    }, [setFormState, cancelUpdate]);

    function handleShowExceptFieldClick(dataKey: string) {
        if (fieldDisplay?.display !== 'show-except') return;
        const currentIndex = fieldDisplay.hiddenFields?.findIndex((f) => f.dataKey === dataKey);
        if (currentIndex === -1) {
            hiddenFields.append({ dataKey });
        } else {
            hiddenFields.remove(currentIndex);
        }
    }

    function handleHideExceptFieldClick(dataKey: string) {
        if (fieldDisplay?.display !== 'hide-except') return;
        const currentIndex = fieldDisplay.shownFields?.findIndex((f) => f.dataKey === dataKey);
        if (currentIndex === -1) {
            shownFields.append({ dataKey });
        } else {
            shownFields.remove(currentIndex);
        }
    }

    return (
        <Form {...form}>
            <form
                onSubmit={form.handleSubmit(onSubmit, onInvalid)}
                className="flex flex-1 flex-col gap-8 items-stretch overflow-hidden"
            >
                <div className="flex flex-1 flex-col gap-8 items-stretch p-5 overflow-scroll">
                    <AppInputField
                        label="Page Title"
                        placeholder="Page Title"
                        control={form.control}
                        name="pageTitle"
                        description="The title displayed in the navigation header."
                    />

                    <AppPromptsField description="What are we asking them to do?" />

                    {/*<AppInputField*/}
                    {/*    control={form.control}*/}
                    {/*    name="dataKey"*/}
                    {/*    label="Data Key"*/}
                    {/*    placeholder="field_name"*/}
                    {/*    description="The name used to reference this value in code."*/}
                    {/*/>*/}

                    <div className="flex flex-col gap-2">
                        <AppEquipmentTypeSelectField
                            control={form.control}
                            name="equipmentTypeId"
                            label="Equipment Type"
                            placeholder="Select an Equipment Type"
                            description="Select an Equipment Type that the user will enter information for"
                            equipmentTypes={equipmentTypes}
                            allowContextSelection
                        />
                    </div>

                    <div className="flex flex-col gap-2">
                        <AppSelectField
                            control={form.control}
                            name="fieldDisplay.display"
                            label="Field Display"
                            description="Control which fields are displayed by default.  Hidden fields show behind an Add More dropdown"
                            options={['all-fields', 'core-only', 'custom-only', 'show-except', 'hide-except']}
                        />

                        {fieldDisplay?.display === 'show-except' && (
                            <div className="flex flex-col gap-1 ml-5">
                                <div className="text-xs font-bold">Fields:</div>
                                {allFields.map((f) => (
                                    <div
                                        key={f.dataKey}
                                        className="flex flex-row items-center justify-between bg-white p-1"
                                    >
                                        <div>{f.label}</div>
                                        <div
                                            onClick={() => handleShowExceptFieldClick(f.dataKey)}
                                            className="bg-secondary px-3 py-1"
                                        >
                                            {(fieldDisplay.hiddenFields || []).map((f) => f.dataKey).includes(f.dataKey)
                                                ? 'Hidden'
                                                : 'Shown'}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {fieldDisplay?.display === 'hide-except' && (
                            <div className="flex flex-col gap-1 ml-5">
                                <div className="text-xs font-bold">Fields:</div>
                                {allFields.map((f) => (
                                    <div
                                        key={f.dataKey}
                                        className="flex flex-row items-center justify-between bg-white p-1"
                                    >
                                        <div>{f.label}</div>
                                        <div
                                            onClick={() => handleHideExceptFieldClick(f.dataKey)}
                                            className="bg-secondary px-3 py-1"
                                        >
                                            {(fieldDisplay.shownFields || []).map((f) => f.dataKey).includes(f.dataKey)
                                                ? 'Shown'
                                                : 'Hidden'}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <AppInputField
                        label="Button Label"
                        placeholder="Continue"
                        control={form.control}
                        name="buttonLabel"
                        description="The label on the button to continue."
                    />
                </div>

                <div className="flex flex-row gap-3 p-5">
                    <Button type="submit" className="flex-1">
                        Save Details
                    </Button>
                    <Button type="button" className="flex-1" variant="outline" onClick={onCancel}>
                        Cancel
                    </Button>
                </div>
            </form>
        </Form>
    );
};

export default EquipmentForm;
